import { useEffect, useRef, useState } from "react";
import { SecretWords, TermBoards } from "../components/games/TermBoards.jsx";
import { useRoom } from "../contexts/RoomContext.jsx";
import { SoundButton, useSound } from "../contexts/SoundContext.jsx";
import { maxTermAttempts } from "../domain/term.js";
import { useTermEngine } from "../hooks/useTermEngine.js";

export function TermMultiplayerPage() {
  const room = useRoom();
  const { tocarSom, mutado } = useSound();
  const mode = Number(room.roomData?.config?.modoTermo) || 1;
  const maxAttempts = maxTermAttempts(mode);
  const timeLimit =
    room.roomData?.config?.tempoRodada === undefined
      ? 0
      : Number(room.roomData.config.tempoRodada);
  const secrets = room.roomData?.termoPalavras || ["TERMO"];
  const [remaining, setRemaining] = useState(timeLimit);
  const correctAudio = useRef(null);
  const wrongAudio = useRef(null);
  const reported = useRef(false);

  const handleFinish = async (won, attempts) => {
    if (reported.current) return;
    reported.current = true;
    tocarSom(won ? correctAudio : wrongAudio, won ? 0.6 : undefined);
    const earned = won ? (maxAttempts - attempts + 1) * 150 : 0;
    try {
      await room.awardPoints(room.user.uid, earned, {
        [`players/${room.user.uid}/termoStatus`]: {
          finalizado: true,
          acertou: won,
          tentativas: attempts,
          pontosGanhos: earned,
        },
      });
    } catch (error) {
      console.error("Erro ao salvar resultado do Termo:", error);
    }
  };

  const engine = useTermEngine({
    secrets,
    maxAttempts,
    resetKey: room.roomData?.rodadaAtual,
    onFinish: handleFinish,
  });

  useEffect(() => {
    document.body.classList.add("term-active");
    return () => document.body.classList.remove("term-active");
  }, []);

  useEffect(() => {
    reported.current = false;
    setRemaining(timeLimit);
  }, [room.roomData?.rodadaAtual, timeLimit]);

  useEffect(() => {
    if (timeLimit === 0 || engine.finished) return undefined;
    const updateTimer = () => {
      const now = Date.now() + room.serverTimeOffset;
      const elapsed = Math.floor(
        (now - room.roomData.timestampInicioRodada) / 1000,
      );
      const next = timeLimit - elapsed;
      setRemaining(Math.max(0, next));
      if (next <= 0) engine.finish(false, engine.guesses.length);
    };
    updateTimer();
    const interval = window.setInterval(updateTimer, 1000);
    return () => window.clearInterval(interval);
  }, [
    timeLimit,
    engine.finished,
    engine.guesses.length,
    room.roomData?.timestampInicioRodada,
    room.serverTimeOffset,
  ]);

  const players = Object.values(room.roomData?.players || {});
  const allFinished = players.every(
    (player) => player.termoStatus?.finalizado === true,
  );

  return (
    <div className="termo-wrapper termo-multiplayer-wrapper">
      <audio
        ref={correctAudio}
        src="/sons/acerto.mp3"
        preload="auto"
        muted={mutado}
      />
      <audio
        ref={wrongAudio}
        src="/sons/erro.mp3"
        preload="auto"
        muted={mutado}
      />
      <header className="term-game-header">
        <div>
          <SoundButton variante="inline" />
          <h2>Rodada {room.roomData?.rodadaAtual || 1}</h2>
        </div>
        <strong className={remaining <= 15 && timeLimit > 0 ? "urgent" : ""}>
          ⏱️ {timeLimit > 0 ? `${remaining}s` : "∞"}
        </strong>
      </header>

      {engine.error && <div className="term-error">{engine.error}</div>}
      {engine.finished ? (
        <div className="term-result-scroll">
          <div className={`card term-result ${engine.won ? "won" : "lost"}`}>
            <h3>{engine.won ? "🎉 Concluído!" : "❌ Fim de Jogo!"}</h3>
            <SecretWords secrets={secrets} />
            <div className="term-round-score">
              <h4>📈 Pontos da Rodada:</h4>
              <ul>
                {players.map((player) => {
                  const status = player.termoStatus;
                  return (
                    <li key={player.id}>
                      <span>
                        {player.username}{" "}
                        {player.id === room.user?.uid && "(Você)"}
                      </span>
                      <strong className={status?.acertou ? "success" : ""}>
                        {status?.finalizado
                          ? status.acertou
                            ? `+${status.pontosGanhos} pts`
                            : "0 pts"
                          : "⏳ Jogando..."}
                      </strong>
                    </li>
                  );
                })}
              </ul>
            </div>
            {room.isHost ? (
              <button
                className="btn btn-primary term-next-round"
                onClick={room.nextRound}
                type="button"
              >
                {allFinished ? "Avançar Rodada ➔" : "Forçar Próxima Rodada ➔"}
              </button>
            ) : (
              <p className="info-box">Aguardando o Host...</p>
            )}
          </div>
        </div>
      ) : (
        <TermBoards
          secrets={secrets}
          maxAttempts={maxAttempts}
          engine={engine}
        />
      )}
    </div>
  );
}
